import React from "react";

import { useReducer, useEffect, useContext, useState, useRef } from "react";
import { useHistory } from "react-router-dom";

// Material UI
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";

import Dialog from "@mui/material/Dialog";

import { Typography } from "@mui/material";

import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";

// My Files
import DragDropFileBox from "./DragDropFileBox";
import FileForUpload from "./FileForUpload";
import { DatabaseContext, UserContext } from "../../../context";
import NoPermissionsMessage from "../../NoPermissionsMessage";
import DialogWithMessage from "../../DialogWithMessage";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function UploadFilesPage(props) {
    const history = useHistory();
    const { users, calendars, folders, groups } = useContext(DatabaseContext);
    const [managers, setManagers] = useState([]);
    const [alertIsVisible, setAlertIsVisible] = useState(false);

    const [fileSizeDialogIsVisible, setFileSizeDialogIsVisible] =
        useState(false);

    const [userCanUpload, setUserCanUpload] = useState();
    const { currentUserData } = useContext(UserContext);

    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [ancestorHubs, setAncestorHubs] = useState([]);

    // Registry for the upload/settings functions of each FileForUpload child.
    // useRef keeps the same object across renders - previously this was
    // recreated on every render and only worked by accident.
    const biRef = useRef({ filesForUpload: [] }).current;

    useEffect(() => {
        let managersArray = [];

        // If Location.folder is set, the user came here from the File Navigator Page
        if (props.folder) {
            // Get managers of the current folder
            props.folder.managers.forEach((managerId) => {
                let manager = users.find((u) => u.key === managerId);

                if (manager) {
                    managersArray.push(manager);
                }
            });

            // Get ancestor hubs, these are used for the groups and users
            let ancestors = folders.filter(
                (f) => f.hub && props.folder.ancestors.includes(f.key),
            );

            // Only include the ancestors that can be managed by the uploading user
            ancestors = ancestors.filter(
                (a) =>
                    a.managers.includes(currentUserData.key) ||
                    currentUserData.role === "System Digital Manager" ||
                    a.users.includes(currentUserData.key),
            );

            // If the current folder is a hub, add that too
            if (props.folder.hub) {
                ancestors.push(props.folder);
            }

            // Get the groups for these ancestors
            ancestors.forEach((ancestorHub) => {
                let matchingGroups = groups.filter(
                    (g) => g.hub === ancestorHub.key,
                );

                ancestorHub.groups = matchingGroups;
            });

            setAncestorHubs(ancestors);

            // Get calendars
            if (
                props.folder.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager" ||
                currentUserData.permissions.uploadEditOwnVideo
            ) {
                setUserCanUpload(true);

                // Find calendars that user can see
                let calendarsArray = [];

                calendars.forEach((calendar) => {
                    let shouldAdd = false;

                    // User should be able to see calendar if it's their personal one, or if they are uploading a file
                    // to the hub for that calendar
                    if (calendar.user === currentUserData.key) {
                        shouldAdd = true;
                    } else if (calendar.hub === props.folder.key) {
                        shouldAdd = true;
                    } else if (props.folder.ancestors.includes(calendar.hub)) {
                        shouldAdd = true;
                    }

                    if (shouldAdd) {
                        calendarsArray.push(calendar);
                    }
                });

                setAvailableCalendars(calendarsArray);
            } else {
                setUserCanUpload(false);
            }
        } else {
            history.replace("/files");
        }

        setManagers(managersArray);

        // eslint-disable-next-line
    }, [
        props.folder,
        users,
        history,
        currentUserData,
        calendars,
        folders,
        groups,
    ]);

    const reducer = (state, action) => {
        switch (action.type) {
            case "SET_DROP_DEPTH":
                return { ...state, dropDepth: action.dropDepth };
            case "SET_IN_DROP_ZONE":
                return { ...state, inDropZone: action.inDropZone };
            case "ADD_FILE_TO_LIST":
                return {
                    ...state,
                    fileList: state.fileList.concat(action.files),
                };
            case "REMOVE_FILE_FROM_LIST":
                return {
                    ...state,
                    fileList: state.fileList.filter(
                        (f) => f.key !== action.file.key,
                    ),
                };
            default:
                return state;
        }
    };

    const [data, dispatch] = useReducer(reducer, {
        dropDepth: 0,
        inDropZone: false,
        fileList: [],
    });

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    function copyFileSettingsToAll(settings) {
        for (let i = 0; i < biRef.filesForUpload.length; i++) {
            biRef.filesForUpload[i].changeSettings(settings);
        }

        setAlertIsVisible(true);
    }

    const handleAlertClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setAlertIsVisible(false);
    };

    function handleUploadButtonClick() {
        for (let i = 0; i < biRef.filesForUpload.length; i++) {
            biRef.filesForUpload[i].startUpload();
        }

        handleClose();
    }

    function showFileSizeMessage() {
        setFileSizeDialogIsVisible(true);
    }

    function handleCloseFileSizeDialog() {
        setFileSizeDialogIsVisible(false);
    }

    function removeUpload(file) {
        dispatch({ type: "REMOVE_FILE_FROM_LIST", file });
    }

    function handleClose() {
        props.onClose();
    }

    return (
        <>
            {userCanUpload && (
                <Dialog
                    fullScreen
                    aria-labelledby="upload-files-title"
                    maxWidth="lg"
                    open={props.open}
                    fullWidth={true}
                    TransitionComponent={Transition}
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: "background.default",
                            },
                        },
                    }}
                >
                    <AppBar
                        position="sticky"
                        elevation={0}
                        sx={{
                            color: "common.white",
                            backgroundColor: "rgb(0, 149, 61)",
                            backgroundImage:
                                "linear-gradient(110deg, rgb(0, 141, 57) 0%, rgb(0, 149, 61) 55%, rgb(0, 139, 57) 100%)",
                            borderBottom:
                                "1px solid rgba(255, 255, 255, 0.16)",
                            boxShadow: "0 2px 12px rgba(0, 70, 30, 0.13)",
                        }}
                    >
                        <Toolbar
                            sx={{
                                minHeight: { xs: 60, sm: 68 },
                                px: { xs: 1, sm: 2.5 },
                                gap: { xs: 0.75, sm: 1.5 },
                            }}
                        >
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="Close upload files"
                                size="large"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.96)",
                                    "&:hover": {
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.14)",
                                    },
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    id="upload-files-title"
                                    variant="h6"
                                    noWrap
                                >
                                    Upload files
                                </Typography>
                                <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{
                                        display: { xs: "none", sm: "block" },
                                        color: "rgba(255, 255, 255, 0.78)",
                                    }}
                                >
                                    {props.folder?.name
                                        ? `Adding to ${props.folder.name}`
                                        : "Prepare files and access settings"}
                                </Typography>
                            </Box>
                            {data.fileList.length > 0 && (
                                <Chip
                                    label={`${data.fileList.length} ${
                                        data.fileList.length === 1
                                            ? "file"
                                            : "files"
                                    }`}
                                    size="small"
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        color: "white",
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.16)",
                                    }}
                                />
                            )}
                            <Button
                                variant="contained"
                                disabled={data.fileList.length === 0}
                                onClick={handleUploadButtonClick}
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    display: { xs: "none", sm: "inline-flex" },
                                    color: "rgb(0, 118, 48)",
                                    backgroundColor: "white",
                                    boxShadow: "none",
                                    "&:hover": {
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.9)",
                                        boxShadow: "none",
                                    },
                                    "&.Mui-disabled": {
                                        color: "rgba(255, 255, 255, 0.46)",
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.12)",
                                    },
                                }}
                            >
                                Start upload
                            </Button>
                        </Toolbar>
                    </AppBar>
                    <Box
                        component="main"
                        sx={{ flex: 1, pb: { xs: 11, sm: 5 } }}
                        onDrop={(e) => handleDrop(e)}
                        onDragOver={(e) => handleDragOver(e)}
                        onDragEnter={(e) => handleDragEnter(e)}
                        onDragLeave={(e) => handleDragLeave(e)}
                    >
                        <Container
                            maxWidth="lg"
                            sx={{
                                px: { xs: 1.5, sm: 3 },
                                py: { xs: 2, sm: 3 },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: {
                                        xs: "flex-start",
                                        sm: "center",
                                    },
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: { xs: 1, sm: 2 },
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2.25,
                                        display: "grid",
                                        placeItems: "center",
                                        flexShrink: 0,
                                        color: "primary.main",
                                        backgroundColor: "action.hover",
                                    }}
                                >
                                    <FolderOutlinedIcon />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h5" component="h2">
                                        Add files
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.35 }}
                                    >
                                        Choose files, then review the name,
                                        access and availability settings before
                                        uploading.
                                    </Typography>
                                </Box>
                            </Box>

                            <DragDropFileBox data={data} dispatch={dispatch} />

                            {data.fileList.length > 0 && (
                                <Box sx={{ mt: { xs: 2.5, sm: 3 } }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            justifyContent: "space-between",
                                            gap: 2,
                                            mb: 1.25,
                                            px: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 800 }}
                                        >
                                            Ready to upload
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {data.fileList.length} selected
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.5,
                                        }}
                                    >
                                        {data.fileList.map((fileMetadata) => (
                                            <FileForUpload
                                                key={fileMetadata.key}
                                                fileMetadata={fileMetadata}
                                                managers={managers}
                                                folder={props.folder}
                                                biRef={biRef}
                                                handleCopyClick={
                                                    copyFileSettingsToAll
                                                }
                                                handleRemoveClick={removeUpload}
                                                calendars={availableCalendars}
                                                ancestorHubs={ancestorHubs}
                                                showFileSizeMessage={
                                                    showFileSizeMessage
                                                }
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Container>
                    </Box>

                    <Box
                        sx={{
                            display: { xs: "flex", sm: "none" },
                            position: "fixed",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 2,
                            p: 1.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            backdropFilter: "blur(12px)",
                        }}
                    >
                        <Button
                            variant="contained"
                            fullWidth
                            disableElevation
                            disabled={data.fileList.length === 0}
                            onClick={handleUploadButtonClick}
                            startIcon={<CloudUploadIcon />}
                            sx={{ minHeight: 46, borderRadius: 2 }}
                        >
                            {data.fileList.length === 0
                                ? "Select files to upload"
                                : `Upload ${data.fileList.length} ${
                                      data.fileList.length === 1
                                          ? "file"
                                          : "files"
                                  }`}
                        </Button>
                    </Box>

                    <Snackbar
                        open={alertIsVisible}
                        autoHideDuration={6000}
                        onClose={handleAlertClose}
                    >
                        <Alert
                            onClose={handleAlertClose}
                            severity="success"
                        >
                            Settings copied to all files
                        </Alert>
                    </Snackbar>
                    <DialogWithMessage
                        open={fileSizeDialogIsVisible}
                        onClose={handleCloseFileSizeDialog}
                        title={"File Size Warning"}
                        message={
                            "One or more of the selected files is larger than 5GB"
                        }
                    />
                </Dialog>
            )}
            {userCanUpload === false && <NoPermissionsMessage />}
        </>
    );
}
